/**
 * UTC instant display helpers.
 *
 * API contract: instants are RFC3339 with Z/+00:00. Older payloads may omit
 * the offset; those are treated as UTC wall clocks.
 *
 * UI contract: absolute timestamps render in the browser's local timezone and
 * always include a short timezone name (e.g. PDT / PST).
 */

const HAS_TZ = /([zZ]|[+-]\d{2}:?\d{2})$/;

const INSTANT_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
};

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};

/** Normalize an ISO-ish instant string so Date parses it as UTC when offset-less. */
export function parseInstant(value: string | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const normalized = HAS_TZ.test(value) ? value : `${value}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format an instant for UI display in the browser's local timezone (with TZ abbr). */
export function formatInstant(
  value: string | null | undefined,
  fallback = '—',
): string {
  const date = parseInstant(value);
  if (!date) return fallback;
  return date.toLocaleString(undefined, INSTANT_FORMAT);
}

/** Format an instant as a local calendar date (no time / zone label). */
export function formatInstantDate(
  value: string | null | undefined,
  fallback = '—',
): string {
  const date = parseInstant(value);
  if (!date) return fallback;
  return date.toLocaleDateString(undefined, DATE_FORMAT);
}

export type FormatRelativeTimeOptions = {
  fallback?: string;
  /** After this many days, fall back to formatInstant (or formatInstantDate if dateOnly). */
  maxDays?: number;
  /** When past maxDays, use date-only formatting instead of full instant. */
  dateOnly?: boolean;
  /** Clock for age math. Defaults to Date.now() so a parent tick can advance the label. */
  nowMs?: number;
};

/**
 * Relative time for recent instants ("5m ago"), falling back to an absolute format.
 * Relative strings omit a zone label; absolute fallbacks include one (unless dateOnly).
 */
export function formatRelativeTime(
  value: string | null | undefined,
  opts?: FormatRelativeTimeOptions,
): string {
  const fallback = opts?.fallback ?? 'Never';
  const maxDays = opts?.maxDays ?? 7;
  const date = parseInstant(value);
  if (!date) return fallback;

  const diffMs = (opts?.nowMs ?? Date.now()) - date.getTime();
  if (diffMs < 0) {
    return opts?.dateOnly
      ? formatInstantDate(value, fallback)
      : formatInstant(value, fallback);
  }

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(diffMs / 86_400_000);
  if (days < maxDays) return `${days}d ago`;

  return opts?.dateOnly
    ? formatInstantDate(value, fallback)
    : formatInstant(value, fallback);
}

/** Today's local calendar date as YYYY-MM-DD (not UTC). */
export function localCalendarDateIso(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
